import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../utils/api';
import { accessToken } from '../utils/helper';
import { DemoAdminDashboard, SuperAdminDashboard, ToneAdminDashboard } from '../dtos/dashboard';

export interface DashboardState {
  superAdminData: SuperAdminDashboard | null;
  demoAdminData: DemoAdminDashboard | null;
  toneAdminData: ToneAdminDashboard | null;
  loading: boolean;
  error: string | null;
}
const initialState: DashboardState = {
  superAdminData: null,
  demoAdminData: null,
  toneAdminData: null,
  loading: false,
  error: null,
};

export const getToneAdminDashboardData = createAsyncThunk('toneDashboard/getData', async () => {
  try {
    const response = await api.get('/home', accessToken());
    return response.data;
  } catch (error) {
    console.error('Error fetching tone dashboard data:', error);
    return { data: {} };
  }
});

export const getToneAdminAnalyticsDashboardData = createAsyncThunk(
  'toneDashboard/getAnalyticsData',
  async () => {
    try {
      const response = await api.get('/home/analytics', accessToken());
      return response.data;
    } catch (error) {
      console.error('Error fetching tone dashboard data:', error);
      return { data: {} };
    }
  },
);

export const getToneAdminRecentsDashboardData = createAsyncThunk(
  'toneDashboard/getAnalyticsData',
  async () => {
    try {
      const response = await api.get('/home/recents', accessToken());
      return response.data;
    } catch (error) {
      console.error('Error fetching tone dashboard data:', error);
      return { data: {} };
    }
  },
);

export const getSuperAdminDashboardData = createAsyncThunk('dashboard/getData', async () => {
  try {
    const response = await api.get('/dashboard', accessToken());
    return response.data;
  } catch (error) {
    console.error('Error fetching location stats:', error);
    return { locations: [], totalUserRequests: [], totalToneSequences: [] };
  }
});
export const getDemoAdminDashboardData = createAsyncThunk('demoDashboard/getData', async (_, { signal }) => {
  try {
    console.log('Making API call to /client-dashboard with token:', accessToken());
    
    const response = await api.get('/client-dashboard', {
      ...accessToken(),
      signal: signal, // Use Redux Toolkit's signal for proper cancellation
    });
    
    console.log('API response received:', response);
    console.log('Response data:', response.data);
    return response.data;
  } catch (error) {
    // Check if the error is due to request cancellation
    if (error instanceof Error && error.name === 'CanceledError') {
      console.log('Request was canceled, this is normal in development mode');
      throw error; // Re-throw to let Redux handle it properly
    }
    
    console.error('Error fetching demo dashboard data:', error);
    console.error('Error response:', (error as any)?.response);
    console.error('Error status:', (error as any)?.status);
    console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
    
    return { data: {} };
  }
});
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSuperAdminDashboardData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSuperAdminDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.superAdminData = action.payload;
      })
      .addCase(getSuperAdminDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch super admin dashboard data';
      })
      .addCase(getDemoAdminDashboardData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDemoAdminDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.demoAdminData = action.payload.data;
      })
      .addCase(getDemoAdminDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch demo dashboard data';
      })
      .addCase(getToneAdminDashboardData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getToneAdminDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.toneAdminData = action.payload.data;
      })
      .addCase(getToneAdminDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tone dashboard data';
      });
  },
});

export default dashboardSlice.reducer;